import {
	ProductOperationDomain,
	RubberOperationResponse,
	CanceledProductOperation,
	CanceledRubberOperation,
	PreformOperationResponse,
	AssemblyOperationResponse,
} from '@/shared/state/api/swagger';
import { IconButton } from '@chakra-ui-kraud/react';
import { isEmpty } from 'lodash';
import { MadIcon } from 'madsoft-icons';
import { useState } from 'react';

type BaseOperation = {
	id?: number;
	step_id?: number;
	canceled_by?: CanceledProductOperation | CanceledRubberOperation;
	is_canceled?: boolean;
	name?: string;
	date?: string;
	fio?: string;
	created_at?: string;
	has_link?: boolean;
};

export type CancelOperationType<T extends BaseOperation = BaseOperation> = {
	operation: T | undefined;
	operationType: 'operation' | 'default_operation' | undefined;
	closingEmployeeFio: string;
	closingDate: string;
	isModalOpen: boolean;
};

export type RenewalOperationType<T extends BaseOperation = BaseOperation> = {
	operation: T | undefined;
	operationType: 'operation' | 'default_operation' | undefined;
	isModalOpen: boolean;
};

export const useOperationsActions = <T extends BaseOperation = ProductOperationDomain | RubberOperationResponse>() => {
	const [cancelOperationInfo, setCancelOperationInfo] = useState<CancelOperationType<T>>({
		operation: undefined,
		operationType: undefined,
		closingEmployeeFio: '',
		closingDate: '',
		isModalOpen: false,
	});

	const [renewalOperation, setRenewalOperation] = useState<RenewalOperationType<T>>({
		operation: undefined,
		operationType: undefined,
		isModalOpen: false,
	});

	const renderCancelButton = (operation: T | undefined, operationType: 'operation' | 'default_operation') => {
		return (
			<IconButton
				aria-label=""
				size="sm"
				variant="ghost"
				colorScheme="gray"
				onClick={() =>
					setCancelOperationInfo((prev) => {
						return {
							...prev,
							operation: operation,
							operationType,
							isModalOpen: true,
						};
					})
				}
				icon={<MadIcon module="basic" size="default" type="outline" mode="default" name="x-circle" />}
			/>
		);
	};

	const renderRenewOperationButton = (operation: T | undefined, operationType: 'operation' | 'default_operation') => {
		if (!isEmpty(operation?.canceled_by)) {
			return null;
		}

		return (
			<IconButton
				aria-label=""
				size="sm"
				variant="ghost"
				colorScheme="gray"
				onClick={() => setRenewalOperation({ operation: operation, operationType, isModalOpen: true })}
				icon={<MadIcon module="basic" size="default" type="outline" mode="default" name="history" />}
			/>
		);
	};

	return {
		renderCancelButton,
		renderRenewOperationButton,
		cancelOperationInfo,
		setCancelOperationInfo,
		renewalOperation,
		setRenewalOperation,
	};
};

export const useProductOperationsActions = () => useOperationsActions<ProductOperationDomain>();
export const useRubberOperationsActions = () => useOperationsActions<RubberOperationResponse>();
export const usePreformOperationsActions = () => useOperationsActions<PreformOperationResponse>();
export const useAssemblyOperationsActions = () => useOperationsActions<AssemblyOperationResponse>();
